﻿import { HTTPMethod, ParsedResult } from "../models/base";

export class ServiceUtils {
    public static IsUnauthorizedResponse(error: any) {
        return error && error.response && error.response.status == 401;
    }
}

export abstract class ServiceBase {
    constructor(
        protected $q: angular.IQService,
        private $http: angular.IHttpService
    ) {
        this.dontSetBearer();
        this.dontSetRoles();
    }
    public doSetRoles(headerSetter: (config: ng.IRequestConfig) => angular.IPromise<ng.IRequestConfig>) {
        this.RoleSetter = headerSetter;
    }
    public dontSetRoles(): void {
        this.RoleSetter = config => this.$q<ng.IRequestConfig>(resolve => resolve(config));
    }
    public doSetBearer(headerSetter: (config: ng.IRequestConfig) => angular.IPromise<ng.IRequestConfig>) {
        this.AuthenticationSetter = headerSetter;
    }
    public dontSetBearer(): void {
        this.AuthenticationSetter = config => this.$q<ng.IRequestConfig>(resolve => resolve(config));
    }
    protected performRequest<RequestType, ResponseType>(method: HTTPMethod, url: string, params: { [key: string]: any } = null, data: RequestType = null, headers: any = null, resolveOnReject: boolean = false): ng.IPromise<ResponseType> {
        return this.internalPerformRequest<RequestType, ResponseType, ResponseType>(method, url, params, data, headers, false, resolveOnReject);
    }

    protected performRequestWithFullResponse<RequestType, ResponseType>(method: HTTPMethod, url: string, params: { [key: string]: any } = null, data: RequestType = null, headers: any = null, resolveOnReject: boolean = false): ng.IPromise<ng.IHttpPromiseCallbackArg<ResponseType>> {
        return this.internalPerformRequest<RequestType, ResponseType, ng.IHttpPromiseCallbackArg<ResponseType>>(method, url, params, data, headers, true, resolveOnReject);
    }

    private internalPerformRequest<RequestType, ResponseType, ResultType>(method: HTTPMethod, url: string, params: { [key: string]: any }, data: RequestType, headers: any, fullResponse: boolean, resolveOnReject: boolean): ng.IPromise<ResultType> {
        var config: angular.IRequestConfig = {
            method: method,
            url: url,
            params: params,
            data: data,
            headers: this.setHeaders(headers),
            transformResponse: this.transformResponse.bind(this),
            transformRequest: this.transformRequest.bind(this)
        };
        return this.$q((resolve: ng.IQResolveReject<ResultType>, reject: ng.IQResolveReject<any>) => {
            this.AuthenticationSetter(config).then(c => this.RoleSetter(c).then(ac => {
                this.$http<ResponseType>(ac)
                    .then(
                    resp => resolve(this.handleResponse<ResultType, ResponseType>(resp, fullResponse)),
                    e => {
                        var handled = this.handleError(e);
                        if (handled !== true)
                            reject(handled);
                        else if (resolveOnReject)
                            resolve(<any>{});
                    })
                    .catch(e => {
                        var handled = this.handleError(e);
                        if (handled !== true)
                            reject(handled);
                        else if (resolveOnReject)
                            resolve(<any>{});
                    });
            }))
        });
    }

    private AuthenticationSetter: (config: ng.IRequestConfig) => angular.IPromise<ng.IRequestConfig>
    private RoleSetter: (config: ng.IRequestConfig) => angular.IPromise<ng.IRequestConfig>

    private httpErrorInterceptors: ([number, ((args: ng.IHttpPromiseCallbackArg<any>, result: ParsedResult) => boolean | any)])[] = [];

    protected addHttpErrorInterceptor(interceptor: (args: ng.IHttpPromiseCallbackArg<any>, result: ParsedResult) => boolean | any, chainPosition: number = 100) {
        if (this.httpErrorInterceptors.some(x => x[0] == chainPosition)) {
            console.log("chainPosition in httpErrorInterceptors taken!");
            return;
        }
        this.httpErrorInterceptors.push([chainPosition, interceptor]);
    }

    private handleError(error: ng.IHttpPromiseCallbackArg<any>) {
        var result: ParsedResult = {};
        for (var interceptor of this.httpErrorInterceptors.sort((a, b) => a[0] - b[0]).map(i => i[1])) {
            var handled = interceptor(error, result);
            if (handled === true)
                return true;
            else if (handled !== false && handled !== undefined)
                return handled;
        }
        result.response = error;
        return result;
    }

    private httpResponseInterceptors: ([number, ((args: ng.IHttpPromiseCallbackArg<any>) => boolean)])[] = [];

    protected addHttpResponseInterceptor(interceptor: (args: ng.IHttpPromiseCallbackArg<any>) => boolean, priority: number = 100) {
        this.httpResponseInterceptors.push([priority, interceptor]);
    }

    private handleResponse<ResultType, ResponseType>(response: ng.IHttpPromiseCallbackArg<ResponseType>, fullResponse: boolean = false): ResultType {
        for (var interceptor of this.httpResponseInterceptors.sort((a, b) => a[0] - b[0])) {
            if (interceptor[1](response) === true)
                return;
        }
        return fullResponse ? <any>response : response.data;
    }

    private headerSetters: ([number, (headers: ng.IHttpRequestConfigHeaders) => ng.IHttpRequestConfigHeaders])[] = [];

    public addHeaderSetter(headerSetter: (headers: ng.IHttpRequestConfigHeaders) => ng.IHttpRequestConfigHeaders, priority: number = 100) {
        this.headerSetters.push([priority, headerSetter]);
    }

    public removeHeaderSetter(priority: number) {
        this.headerSetters = this.headerSetters.filter(hs => hs[0] != priority);
    }

    private setHeaders(headers: ng.IHttpRequestConfigHeaders): ng.IHttpRequestConfigHeaders {
        headers = headers || {};
        this.headerSetters.sort((a, b) => a[0] - b[0]).forEach(hs => headers = hs[1](headers));
        return headers;
    }

    private requestTransformers: ([number, (data: any, headersGetter: ng.IHttpHeadersGetter) => { data: any, continue: boolean }])[] = [];

    public addRequestTransformers(requestTransformer: (data: any, headersGetter: ng.IHttpHeadersGetter) => { data: any, continue: boolean }, priority: number = 100) {
        this.requestTransformers.push([priority, requestTransformer]);
    }

    private transformRequest(data: any, headersGetter: ng.IHttpHeadersGetter): any {
        for (var transformer of this.requestTransformers.sort((a, b) => a[0] - b[0])) {
            var res = transformer[1](data, headersGetter);
            data = res.data;
            if (!res.continue)
                break;
        }
        return data;
    }
    private responseTransformers: ([number, (data: any, headersGetter: ng.IHttpHeadersGetter, status: number) => { data: any, continue: boolean }])[] = [];

    public addResponseTransformer(responseTransformer: (data: any, headersGetter: ng.IHttpHeadersGetter, status: number) => { data: any, continue: boolean }, priority: number = 100) {
        this.responseTransformers.push([priority, responseTransformer]);
    }

    private transformResponse(data: any, headersGetter: ng.IHttpHeadersGetter, status: number): any {
        for (var transformer of this.responseTransformers.sort((a, b) => a[0] - b[0])) {
            var res = transformer[1](data, headersGetter, status);
            data = res.data;
            if (!res.continue)
                break;
        }
        return data;
    }
}
